import React, {
  ClipboardEvent,
  DragEvent,
  FC,
  forwardRef,
  KeyboardEvent,
  SVGProps,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposerAlignCenterIcon,
  ComposerAlignLeftIcon,
  ComposerAlignRightIcon,
  ComposerBoldIcon,
  ComposerCheckIcon,
  ComposerItalicIcon,
  ComposerLinkIcon,
  ComposerStrikethroughIcon,
  ComposerUnderlineIcon,
} from 'static';

import { applyAutoLinks, normalizeLinkUrl } from './autoLink';
import { bodyToEditorHtml, serializeEditorContent } from './serialize';
import {
  StyledEditable,
  StyledEditorWrapper,
  StyledLinkForm,
  StyledLinkInput,
  StyledPlaceholder,
  StyledToolbar,
  StyledToolbarButton,
  StyledToolbarDivider,
} from './styled';

type FormatCommand =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikeThrough'
  | 'justifyLeft'
  | 'justifyCenter'
  | 'justifyRight';

type ToolbarItem = {
  command: FormatCommand;
  labelKey: string;
  shortcut?: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
};

const TOOLBAR_GROUPS: ToolbarItem[][] = [
  [
    {
      command: 'bold',
      labelKey: 'email.format.bold',
      shortcut: 'B',
      Icon: ComposerBoldIcon,
    },
    {
      command: 'italic',
      labelKey: 'email.format.italic',
      shortcut: 'I',
      Icon: ComposerItalicIcon,
    },
    {
      command: 'underline',
      labelKey: 'email.format.underline',
      shortcut: 'U',
      Icon: ComposerUnderlineIcon,
    },
    {
      command: 'strikeThrough',
      labelKey: 'email.format.strikethrough',
      Icon: ComposerStrikethroughIcon,
    },
  ],
  [
    {
      command: 'justifyLeft',
      labelKey: 'email.format.alignLeft',
      Icon: ComposerAlignLeftIcon,
    },
    {
      command: 'justifyCenter',
      labelKey: 'email.format.alignCenter',
      Icon: ComposerAlignCenterIcon,
    },
    {
      command: 'justifyRight',
      labelKey: 'email.format.alignRight',
      Icon: ComposerAlignRightIcon,
    },
  ],
];

const TOOLBAR_HEIGHT = 40;
const TOOLBAR_GAP = 10;

const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let current = node?.parentElement ?? null;
  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') return current;
    current = current.parentElement;
  }
  return null;
};

export const MOD_KEY_LABEL =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? '⌘'
    : 'Ctrl';

type ToolbarState = {
  visible: boolean;
  top: number;
  left: number;
};

const HIDDEN_TOOLBAR: ToolbarState = { visible: false, top: 0, left: 0 };

const queryCommandState = (command: string) => {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
};

export interface RichTextEditorHandle {
  focus: () => void;
}

interface RichTextEditorProps {
  id?: string;
  value: string;
  placeholder: string;
  hasError?: boolean;
  fill?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  ({ id, value, placeholder, hasError, fill, onChange, onBlur }, ref) => {
    const { t } = useTranslation();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const linkInputRef = useRef<HTMLInputElement>(null);
    const savedRangeRef = useRef<Range | null>(null);
    const linkModeRef = useRef(false);
    const lastEmittedRef = useRef<string | null>(null);

    const [isEmpty, setIsEmpty] = useState(true);
    const [toolbar, setToolbar] = useState<ToolbarState>(HIDDEN_TOOLBAR);
    const [activeFormats, setActiveFormats] = useState<
      Partial<Record<FormatCommand | 'link', boolean>>
    >({});
    const [linkMode, setLinkMode] = useState(false);
    const [linkValue, setLinkValue] = useState('');
    const [linkInvalid, setLinkInvalid] = useState(false);

    const getSelectionLink = useCallback((): HTMLAnchorElement | null => {
      const selection = window.getSelection();
      const editor = editorRef.current;
      if (!selection || !selection.anchorNode || !editor) return null;
      const node =
        selection.anchorNode.nodeType === Node.ELEMENT_NODE
          ? (selection.anchorNode as Element)
          : selection.anchorNode.parentElement;
      const link = node?.closest('a');
      return link && editor.contains(link) ? link : null;
    }, []);

    const updateToolbar = useCallback(() => {
      if (linkModeRef.current) return;

      const editor = editorRef.current;
      const wrapper = wrapperRef.current;
      const selection = window.getSelection();

      if (
        !editor ||
        !wrapper ||
        !selection ||
        selection.rangeCount === 0 ||
        selection.isCollapsed ||
        !editor.contains(selection.anchorNode) ||
        !editor.contains(selection.focusNode)
      ) {
        setToolbar((prev) =>
          prev.visible ? { ...prev, visible: false } : prev,
        );
        return;
      }

      const rect = selection.getRangeAt(0).getBoundingClientRect();
      if (!rect.width && !rect.height) return;

      const scroller = getScrollParent(wrapper);
      const scrollRect = scroller?.getBoundingClientRect();
      const selectionHidden =
        !!scrollRect &&
        (rect.bottom < scrollRect.top || rect.top > scrollRect.bottom);
      if (selectionHidden) {
        setToolbar((prev) =>
          prev.visible ? { ...prev, visible: false } : prev,
        );
        return;
      }

      const card =
        wrapper.closest('section')?.getBoundingClientRect() ??
        scrollRect ??
        wrapper.getBoundingClientRect();
      const horizontal = scrollRect ?? card;
      const halfWidth = (toolbarRef.current?.offsetWidth || 320) / 2;
      const center = rect.left + rect.width / 2;
      const left = Math.min(
        Math.max(center, horizontal.left + halfWidth),
        Math.max(horizontal.left + halfWidth, horizontal.right - halfWidth),
      );
      const needed = TOOLBAR_HEIGHT + TOOLBAR_GAP;
      const above = rect.top - needed;
      const below = rect.bottom + TOOLBAR_GAP;
      const top =
        above >= card.top
          ? above
          : below + TOOLBAR_HEIGHT <= card.bottom
          ? below
          : Math.max(card.top, above);

      setToolbar({ visible: true, top, left });
      setActiveFormats({
        bold: queryCommandState('bold'),
        italic: queryCommandState('italic'),
        underline: queryCommandState('underline'),
        strikeThrough: queryCommandState('strikeThrough'),
        justifyLeft: queryCommandState('justifyLeft'),
        justifyCenter: queryCommandState('justifyCenter'),
        justifyRight: queryCommandState('justifyRight'),
        link: !!getSelectionLink(),
      });
    }, [getSelectionLink]);

    const emitChange = useCallback(
      (forceAll?: boolean) => {
        const editor = editorRef.current;
        if (!editor) return;
        if (!linkModeRef.current) applyAutoLinks(editor, forceAll === true);
        const html = serializeEditorContent(editor);
        const markup = editor.innerHTML;
        setIsEmpty(markup === '' || markup === '<br>');
        lastEmittedRef.current = html;
        onChange(html);
      },
      [onChange],
    );

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor || value === lastEmittedRef.current) return;
      editor.innerHTML = bodyToEditorHtml(value);
      if (!linkModeRef.current) applyAutoLinks(editor, true);
      const html = serializeEditorContent(editor);
      lastEmittedRef.current = html;
      setIsEmpty(!html.trim());
      if (html !== value) onChange(html);
    }, [value, onChange]);

    useEffect(() => {
      const scroller = getScrollParent(wrapperRef.current);
      document.addEventListener('selectionchange', updateToolbar);
      window.addEventListener('resize', updateToolbar);
      scroller?.addEventListener('scroll', updateToolbar, { passive: true });
      return () => {
        document.removeEventListener('selectionchange', updateToolbar);
        window.removeEventListener('resize', updateToolbar);
        scroller?.removeEventListener('scroll', updateToolbar);
      };
    }, [updateToolbar]);

    useEffect(() => {
      if (linkMode) linkInputRef.current?.focus();
    }, [linkMode]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.focus();
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      },
    }));

    const applyFormat = (command: FormatCommand) => {
      document.execCommand('styleWithCSS', false, 'false');
      document.execCommand(command, false);
      emitChange();
      updateToolbar();
    };

    const exitLinkMode = (): Range | null => {
      const range = savedRangeRef.current;
      savedRangeRef.current = null;
      linkModeRef.current = false;
      setLinkMode(false);
      setLinkInvalid(false);
      return range;
    };

    const returnToEditor = (range: Range | null) => {
      editorRef.current?.focus();
      const selection = window.getSelection();
      if (!selection || !range) return;
      selection.removeAllRanges();
      selection.addRange(range);
    };

    const cancelLinkMode = () => {
      returnToEditor(exitLinkMode());
      updateToolbar();
    };

    const handleLinkBlur = () => {
      if (!linkModeRef.current) return;
      exitLinkMode();
      setToolbar((prev) => ({ ...prev, visible: false }));
    };

    const openLinkMode = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        return;
      }
      if (getSelectionLink()) {
        document.execCommand('unlink', false);
        emitChange();
        updateToolbar();
        return;
      }
      savedRangeRef.current = selection.getRangeAt(0).cloneRange();
      linkModeRef.current = true;
      setLinkValue('');
      setLinkInvalid(false);
      setLinkMode(true);
    };

    const applyLink = () => {
      if (!linkValue.trim()) {
        cancelLinkMode();
        return;
      }
      const url = normalizeLinkUrl(linkValue);
      if (!url) {
        setLinkInvalid(true);
        return;
      }
      returnToEditor(exitLinkMode());
      document.execCommand('createLink', false, url);
      emitChange();
      updateToolbar();
    };

    const handleLinkKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        applyLink();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        cancelLinkMode();
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      const isMod = event.metaKey || event.ctrlKey;
      if (isMod && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openLinkMode();
      }
    };

    const insertPlainText = (text: string, linkifyAll = false) => {
      if (!text) return;
      document.execCommand('insertText', false, text);
      emitChange(linkifyAll);
    };

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      insertPlainText(event.clipboardData.getData('text/plain'), true);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
      if (event.dataTransfer.types.includes('Files')) return;
      event.preventDefault();
      const doc = document as Document & {
        caretRangeFromPoint?: (x: number, y: number) => Range | null;
      };
      const range = doc.caretRangeFromPoint?.(event.clientX, event.clientY);
      if (range) {
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      insertPlainText(event.dataTransfer.getData('text/plain'), true);
    };

    const preventFocusLoss = (event: React.MouseEvent) =>
      event.preventDefault();

    return (
      <StyledEditorWrapper ref={wrapperRef} $fill={fill}>
        <StyledToolbar
          ref={toolbarRef}
          role="toolbar"
          aria-label={t('email.format.toolbar')}
          visible={toolbar.visible || linkMode}
          top={toolbar.top}
          left={toolbar.left}
          onMouseDown={preventFocusLoss}
        >
          {linkMode ? (
            <StyledLinkForm>
              <StyledLinkInput
                ref={linkInputRef}
                value={linkValue}
                invalid={linkInvalid}
                placeholder={t('email.format.linkPlaceholder')}
                onChange={(e) => {
                  setLinkValue(e.target.value);
                  setLinkInvalid(false);
                }}
                onKeyDown={handleLinkKeyDown}
                onBlur={handleLinkBlur}
                onMouseDown={(e) => e.stopPropagation()}
              />
              <StyledToolbarButton
                type="button"
                aria-label={t('email.format.applyLink')}
                title={t('email.format.applyLink')}
                onClick={applyLink}
              >
                <ComposerCheckIcon />
              </StyledToolbarButton>
            </StyledLinkForm>
          ) : (
            <>
              {TOOLBAR_GROUPS.map((group, index) => (
                <React.Fragment key={group[0].command}>
                  {index > 0 && <StyledToolbarDivider />}
                  {group.map(({ command, labelKey, shortcut, Icon }) => {
                    const label = shortcut
                      ? `${t(labelKey)} (${MOD_KEY_LABEL}+${shortcut})`
                      : t(labelKey);
                    return (
                      <StyledToolbarButton
                        key={command}
                        type="button"
                        active={!!activeFormats[command]}
                        aria-label={label}
                        aria-pressed={!!activeFormats[command]}
                        title={label}
                        onClick={() => applyFormat(command)}
                      >
                        <Icon />
                      </StyledToolbarButton>
                    );
                  })}
                </React.Fragment>
              ))}
              <StyledToolbarDivider />
              <StyledToolbarButton
                type="button"
                active={!!activeFormats.link}
                aria-pressed={!!activeFormats.link}
                aria-label={
                  activeFormats.link
                    ? t('email.format.removeLink')
                    : `${t('email.format.link')} (${MOD_KEY_LABEL}+K)`
                }
                title={
                  activeFormats.link
                    ? t('email.format.removeLink')
                    : `${t('email.format.link')} (${MOD_KEY_LABEL}+K)`
                }
                onClick={openLinkMode}
              >
                <ComposerLinkIcon />
              </StyledToolbarButton>
            </>
          )}
        </StyledToolbar>
        {isEmpty && <StyledPlaceholder>{placeholder}</StyledPlaceholder>}
        <StyledEditable
          id={id}
          ref={editorRef}
          $fill={fill}
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder}
          aria-invalid={hasError}
          spellCheck
          onInput={() => emitChange()}
          onKeyDown={handleKeyDown}
          onKeyUp={updateToolbar}
          onMouseUp={updateToolbar}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onBlur={() => {
            emitChange(true);
            onBlur?.();
          }}
        />
      </StyledEditorWrapper>
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
